import { User, GoogleUser, DiscordUser } from '../types';

const STORAGE_KEY = 'autocorrect_adventure_user';
const TOKEN_STORAGE_KEY = 'autocorrect_adventure_token';
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const DISCORD_CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID || '';
const DISCORD_REDIRECT_URI = import.meta.env.VITE_DISCORD_REDIRECT_URI || '';

// Declare Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              type?: 'standard' | 'icon';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: number;
            }
          ) => void;
          prompt: () => void;
          revoke: (email: string, callback: () => void) => void;
        };
      };
    };
  }
}

// Decode JWT token from Google
function decodeJwt(token: string): GoogleUser | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    return {
      id: payload.sub,
      email: payload.email,
      name: payload.name,
      picture: payload.picture,
    };
  } catch (e) {
    console.error('Failed to decode JWT:', e);
    return null;
  }
}

// Load user from localStorage
export function loadStoredUser(): User | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load stored user:', e);
  }
  return null;
}

// Load JWT token from localStorage
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    console.error('Failed to load stored token:', e);
  }
  return null;
}

// Save user to localStorage
export function saveUser(user: User): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } catch (e) {
    console.error('Failed to save user:', e);
  }
}

// Save JWT token to localStorage
export function saveToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (e) {
    console.error('Failed to save token:', e);
  }
}

// Clear user from localStorage
export function clearStoredUser(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear stored user:', e);
  }
}

// Initialize Google Sign-In
export function initializeGoogleSignIn(
  onSuccess: (user: GoogleUser, token: string) => void,
  buttonElement?: HTMLElement | null
): void {
  if (!GOOGLE_CLIENT_ID) {
    console.warn('Google Client ID not configured. Set VITE_GOOGLE_CLIENT_ID in .env');
    return;
  }

  if (!window.google) {
    console.warn('Google Identity Services not loaded');
    return;
  }

  try {
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (response) => {
        const user = decodeJwt(response.credential);
        if (user) {
          saveUser(user);
          saveToken(response.credential);
          onSuccess(user, response.credential);
        }
      },
      auto_select: true,
    });

    if (buttonElement) {
      window.google.accounts.id.renderButton(buttonElement, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        shape: 'rectangular',
      });
    }

    // Show One Tap prompt
    window.google.accounts.id.prompt();
  } catch (err) {
    console.error('Failed to initialize Google Sign-In:', err);
  }
}

// Sign out
export function signOut(email: string, onComplete: () => void): void {
  clearStoredUser();
  
  // Only attempt to revoke Google session if Google Identity Services is fully loaded
  // and the user signed in with Google (has an email)
  if (window.google?.accounts?.id && email && GOOGLE_CLIENT_ID) {
    try {
      // Re-initialize before revoking to avoid "Attempt to call revoke() before initialize()" error
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: () => {}, // No-op callback
      });
      
      window.google.accounts.id.revoke(email, () => {
        onComplete();
      });
    } catch (err) {
      console.warn('Failed to revoke Google session:', err);
      // Still complete the logout even if revoke fails
      onComplete();
    }
  } else {
    // For Discord users or when Google isn't loaded, just complete immediately
    onComplete();
  }
}

// Check if Google Client ID is configured
export function isGoogleAuthConfigured(): boolean {
  return !!GOOGLE_CLIENT_ID;
}

// Discord OAuth Functions

// Check if Discord Client ID is configured
export function isDiscordAuthConfigured(): boolean {
  return !!DISCORD_CLIENT_ID && !!DISCORD_REDIRECT_URI;
}

// Initiate Discord OAuth flow
export function initiateDiscordSignIn(): void {
  if (!DISCORD_CLIENT_ID || !DISCORD_REDIRECT_URI) {
    console.warn('Discord Client ID or Redirect URI not configured. Set VITE_DISCORD_CLIENT_ID and VITE_DISCORD_REDIRECT_URI in .env');
    return;
  }

  const state = Math.random().toString(36).substring(7);
  sessionStorage.setItem('discord_oauth_state', state);

  const params = new URLSearchParams({
    client_id: DISCORD_CLIENT_ID,
    redirect_uri: DISCORD_REDIRECT_URI,
    response_type: 'code',
    scope: 'identify email',
    state: state,
  });

  window.location.href = `https://discord.com/api/oauth2/authorize?${params.toString()}`;
}

// Handle Discord OAuth callback (called from callback page)
export async function handleDiscordCallback(code: string, state: string): Promise<User | null> {
  try {
    // Verify state
    const storedState = sessionStorage.getItem('discord_oauth_state');
    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }
    sessionStorage.removeItem('discord_oauth_state');

    // Exchange code for token and user info via backend
    const response = await fetch('/api/auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange Discord code');
    }

    const data = await response.json();
    const user: DiscordUser = {
      id: data.user.id,
      email: data.user.email,
      name: data.user.username,
      picture: data.user.avatar 
        ? `https://cdn.discordapp.com/avatars/${data.user.id}/${data.user.avatar}.png`
        : `https://cdn.discordapp.com/embed/avatars/${parseInt(data.user.discriminator) % 5}.png`,
      provider: 'discord',
      discriminator: data.user.discriminator,
    };

    saveUser(user);
    saveToken(data.token);

    return user;
  } catch (error) {
    console.error('Discord callback error:', error);
    return null;
  }
}
